/**
 * AuthGuard - Protected Route Wrapper
 *
 * Wraps pages that require authentication. Redirects to sign-in
 * if the user is not authenticated. Also initializes workspace
 * data when user becomes authenticated.
 *
 * @example
 * import { AuthGuard } from '@/components/auth-guard';
 *
 * // In router:
 * "/dashboard": () => AuthGuard(DashboardPage())
 */

import { html, attr, OneOfValue, Async } from '@tempots/dom'
import type { TNode } from '@tempots/dom'
import { NavigationService } from '@tempots/ui'
import { Icon } from '@tempots/beatui'
import { authStore } from '@/services/gateway'
import { Workspace } from '@/state/workspace'

export interface AuthGuardOptions {
  /** URL to redirect to when not authenticated (default: /signin) */
  redirectTo?: string
  /** Custom loading component */
  loadingComponent?: () => TNode
  unauthenticatedComponent?: () => TNode
}

/**
 * Wrap a page component to require authentication
 */
export function AuthGuard(
  {
    redirectTo = '/signin',
    loadingComponent = LoadingScreen,
    unauthenticatedComponent,
  }: AuthGuardOptions,
  content: () => TNode
): TNode {
  return OneOfValue(authStore.status, {
    loading: loadingComponent,
    authenticated: () => {
      // Initialize workspace data when authenticated
      // This is idempotent - only runs once
      return Async(Workspace.loadWorkspace(), {
        then: content,
        pending: loadingComponent,
        error: (error) => {
          console.error(error)
          return html.div(attr.class('text-red-500'), 'Error loading workspace')
        },
      })
    },
    unauthenticated:
      unauthenticatedComponent ??
      (() => {
        const returnUrl = encodeURIComponent(window.location.pathname)
        NavigationService.navigate(`${redirectTo}?returnUrl=${returnUrl}`)
        return null
      }),
  })
}

/**
 * Default loading screen shown while checking auth
 */
function LoadingScreen(): TNode {
  return html.div(
    attr.class('min-h-screen flex items-center justify-center bg-gray-50'),
    html.div(
      attr.class('text-center'),
      html.div(
        attr.class(
          'w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center mx-auto'
        ),
        Icon(
          { icon: 'mdi:loading' },
          attr.class('w-6 h-6 text-brand-600 animate-spin')
        )
      ),
      html.p(attr.class('mt-4 text-sm text-gray-500'), 'Loading...')
    )
  )
}
